// src/config/axiosConfig.jsx
import axios from "axios";

/** API base URL: set VITE_API_BASE in .env for production (e.g. https://api.yourdomain.com) */
export const apiBase =
  import.meta.env.VITE_API_BASE || "http://localhost:8000";

const api = axios.create({
  baseURL: apiBase,
  withCredentials: true,
});

// --- CSRF (Django defaults)
api.defaults.xsrfCookieName = "csrftoken";
api.defaults.xsrfHeaderName = "X-CSRFToken";

// Fallback cookie getter (only used if Axios didn't auto-set the header)
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
};

// --- dev helpers
const toCurl = (cfg) => {
  const url = `${cfg.baseURL || ""}${cfg.url}`;
  const h = Object.entries(cfg.headers || {})
    .map(([k, v]) => `-H ${JSON.stringify(`${k}: ${v}`)}`)
    .join(" ");
  const data =
    cfg.data && typeof cfg.data === "string"
      ? `--data ${JSON.stringify(cfg.data)}`
      : cfg.data instanceof FormData
        ? `--form ...`
        : cfg.data
          ? `--data ${JSON.stringify(cfg.data)}`
          : "";
  const method = (cfg.method || "get").toUpperCase();
  return `curl -X ${method} ${h} ${data} "${url}"`;
};

// --- request
api.interceptors.request.use((cfg) => {
  cfg.meta = { started: performance.now(), id: Math.random().toString(36).slice(2) };


  // Ensure CSRF + headers on unsafe methods
  const method = (cfg.method || "get").toUpperCase();
  const unsafe = ["POST", "PUT", "PATCH", "DELETE"].includes(method);
  if (unsafe) {
    // Axios will set X-CSRFToken automatically from xsrfCookieName/xsrfHeaderName
    // but we add a fallback in case something strips it.
    const alreadySet = cfg.headers?.[api.defaults.xsrfHeaderName];
    if (!alreadySet) {
      const csrf =
        getCookie(api.defaults.xsrfCookieName) ||
        getCookie("CSRF-TOKEN") ||
        getCookie("XSRF-TOKEN") ||
        getCookie("JWT_CSRF"); // add your custom name here if different
      if (csrf) {
        cfg.headers[api.defaults.xsrfHeaderName] = csrf;
      }
    }
    cfg.headers["X-Requested-With"] = "XMLHttpRequest";
    if (!cfg.headers["Content-Type"] && !(cfg.data instanceof FormData)) {
      cfg.headers["Content-Type"] = "application/json";
    }
  }


  return cfg;
});

// --- silent token refresh on 401
const REFRESH_URL = "/accounts/api/v1/token/refresh/";
// Endpoints that must NOT trigger a refresh retry (they handle auth themselves).
const AUTH_FREE_PATHS = [
  REFRESH_URL,
  "/accounts/api/v1/login/",
  "/accounts/api/v1/logout/",
];

// Single-flight: many requests can 401 at once; only refresh once.
let refreshPromise = null;

// --- response
api.interceptors.response.use(
  (res) => {
    return res;
  },
  async (err) => {
    const cfg = err.config || {};
    const status = err.response?.status;
    const url = cfg.url || "";
    const isAuthFree = AUTH_FREE_PATHS.some((p) => url.includes(p));

    // Access token likely expired: try a one-time silent refresh, then retry.
    if (status === 401 && !cfg._retry && !isAuthFree) {
      cfg._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = api
            .post(REFRESH_URL, {}, { withCredentials: true })
            .finally(() => {
              refreshPromise = null;
            });
        }
        await refreshPromise;
        // Refresh succeeded; new cookies are set — replay the original request.
        return api(cfg);
      } catch {
        // Refresh failed; fall through to the redirect below.
      }
    }

    // Unrecoverable 401 (no/expired refresh): clear state and go to login —
    // unless the visitor is on a public page (login, password reset, signup),
    // where an anonymous 401 from session hydration is expected and must not
    // yank them off the page.
    if (status === 401) {
      localStorage.removeItem("JWT_COOKIE_MUSIC_ONLINE");
      const currentPath = window.location.pathname;
      const PUBLIC_PATHS = ["/login", "/password-reset", "/signup", "/register", "/activate"];
      const onPublicPage =
        currentPath === "/" ||
        PUBLIC_PATHS.some(
          (p) => currentPath === p || currentPath.startsWith(p + "/")
        );
      if (!onPublicPage) {
        window.location.href = "/login";
        return Promise.reject(err);
      }
    }

    return Promise.reject(err);
  }
);

export default api;
